import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const IdariIslerIstatistiklerPage = () => {
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    completed: 0,
    rejected: 0
  });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toasts, addToast, removeToast } = useToast();

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [statsResponse, requestsResponse] = await Promise.all([
        api.get('/api/administrative/stats'),
        api.get('/api/administrative/requests')
      ]);

      if (statsResponse.success && statsResponse.data) {
        setStats(statsResponse.data);
      }

      if (requestsResponse.success) {
        setRequests(requestsResponse.requests);
      }
    } catch (error) {
      console.error('İstatistikler yüklenirken hata:', error);
      addToast('İstatistikler yüklenirken bir hata oluştu', 'error', 5000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const getCategoryStats = () => {
    const categoryStats = {
      stationery: 0,
      phone: 0,
      computer: 0,
      other: 0
    };

    requests.forEach(request => {
      if (categoryStats.hasOwnProperty(request.category)) {
        categoryStats[request.category]++;
      }
    });

    return categoryStats;
  };

  const getDepartmentStats = () => {
    const departmentStats = {};
    
    requests.forEach(request => {
      const dept = request.department || 'other';
      departmentStats[dept] = (departmentStats[dept] || 0) + 1;
    });

    return departmentStats;
  };

  const getPriorityStats = () => {
    const priorityStats = {
      low: 0,
      normal: 0,
      high: 0,
      urgent: 0
    };

    requests.forEach(request => {
      if (priorityStats.hasOwnProperty(request.priority)) {
        priorityStats[request.priority]++;
      }
    });

    return priorityStats;
  };

  const getStatusPercentage = (count) => {
    return stats && stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
  };

  const categoryStats = getCategoryStats();
  const departmentStats = getDepartmentStats();
  const priorityStats = getPriorityStats();

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <div className="modern-hr-header-content">
            <div className="modern-hr-header-text">
              <h1>İdari İşler İstatistikleri</h1>
              <p>Talep istatistiklerini ve analizlerini görüntüleyin</p>
            </div>
            <div className="modern-hr-header-actions">
              <div className="modern-hr-stats">
                <div className="modern-hr-stat-item">
                  <i className="bx bx-bar-chart"></i>
                  <span>Analiz</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Genel İstatistikler */}
        <div className="row mb-4">
          {/* Üst Satır - 3 Box */}
          <div className="col-lg-4 col-md-6 mb-3">
            <div className="modern-stats-card">
              <div className="modern-stats-card-icon bg-primary">
                <i className="bx bx-file-plus"></i>
              </div>
              <div className="modern-stats-card-content">
                <h3>{stats?.total || 0}</h3>
                <p>Toplam Talep</p>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mb-3">
            <div className="modern-stats-card">
              <div className="modern-stats-card-icon bg-warning">
                <i className="bx bx-time"></i>
              </div>
              <div className="modern-stats-card-content">
                <h3>{stats?.pending || 0}</h3>
                <p>Beklemede</p>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mb-3">
            <div className="modern-stats-card">
              <div className="modern-stats-card-icon bg-info">
                <i className="bx bx-check"></i>
              </div>
              <div className="modern-stats-card-content">
                <h3>{stats?.approved || 0}</h3>
                <p>Onaylandı</p>
              </div>
            </div>
          </div>
        </div>

        {/* Alt Satır - 3 Box */}
        <div className="row mb-4">
          <div className="col-lg-4 col-md-6 mb-3">
            <div className="modern-stats-card">
              <div className="modern-stats-card-icon bg-success">
                <i className="bx bx-check-circle"></i>
              </div>
              <div className="modern-stats-card-content">
                <h3>{stats?.completed || 0}</h3>
                <p>Tamamlandı</p>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mb-3">
            <div className="modern-stats-card">
              <div className="modern-stats-card-icon bg-danger">
                <i className="bx bx-x-circle"></i>
              </div>
              <div className="modern-stats-card-content">
                <h3>{stats?.rejected || 0}</h3>
                <p>Reddedildi</p>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mb-3">
            <div className="modern-stats-card">
              <div className="modern-stats-card-icon bg-secondary">
                <i className="bx bx-trending-up"></i>
              </div>
              <div className="modern-stats-card-content">
                <h3>{getStatusPercentage(stats?.completed || 0)}%</h3>
                <p>Tamamlanma Oranı</p>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Kategori İstatistikleri */}
          <div className="col-lg-4 col-md-6 mb-4">
            <div className="modern-hr-widget">
              <div className="modern-hr-widget-header">
                <div className="modern-hr-widget-title">
                  <i className="bx bx-category"></i>
                  <h4>Kategori Dağılımı</h4>
                </div>
              </div>
              <div className="modern-hr-widget-body">
                <div className="modern-stats-list">
                  <div className="modern-stats-item">
                    <div className="modern-stats-item-info">
                      <span className="modern-stats-item-label">Kırtasiye</span>
                      <span className="modern-stats-item-value">{categoryStats.stationery}</span>
                    </div>
                    <div className="modern-stats-item-bar">
                      <div 
                        className="modern-stats-item-progress bg-primary" 
                        style={{ width: `${getStatusPercentage(categoryStats.stationery)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="modern-stats-item">
                    <div className="modern-stats-item-info">
                      <span className="modern-stats-item-label">Telefon</span>
                      <span className="modern-stats-item-value">{categoryStats.phone}</span>
                    </div>
                    <div className="modern-stats-item-bar">
                      <div 
                        className="modern-stats-item-progress bg-info" 
                        style={{ width: `${getStatusPercentage(categoryStats.phone)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="modern-stats-item">
                    <div className="modern-stats-item-info">
                      <span className="modern-stats-item-label">Bilgisayar</span>
                      <span className="modern-stats-item-value">{categoryStats.computer}</span>
                    </div>
                    <div className="modern-stats-item-bar">
                      <div 
                        className="modern-stats-item-progress bg-success" 
                        style={{ width: `${getStatusPercentage(categoryStats.computer)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="modern-stats-item">
                    <div className="modern-stats-item-info">
                      <span className="modern-stats-item-label">Diğer</span>
                      <span className="modern-stats-item-value">{categoryStats.other}</span>
                    </div>
                    <div className="modern-stats-item-bar">
                      <div 
                        className="modern-stats-item-progress bg-warning" 
                        style={{ width: `${getStatusPercentage(categoryStats.other)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Departman İstatistikleri */}
          <div className="col-lg-4 col-md-6 mb-4">
            <div className="modern-hr-widget">
              <div className="modern-hr-widget-header">
                <div className="modern-hr-widget-title">
                  <i className="bx bx-building"></i>
                  <h4>Departman Dağılımı</h4>
                </div>
              </div>
              <div className="modern-hr-widget-body">
                <div className="modern-stats-list">
                  {Object.entries(departmentStats).map(([dept, count]) => (
                    <div key={dept} className="modern-stats-item">
                      <div className="modern-stats-item-info">
                        <span className="modern-stats-item-label">
                          {dept === 'hr' ? 'İnsan Kaynakları' :
                           dept === 'it' ? 'Bilgi İşlem' :
                           dept === 'finance' ? 'Mali İşler' :
                           dept === 'procurement' ? 'Satın Alma' :
                           dept === 'operations' ? 'Operasyon' : 'Diğer'}
                        </span>
                        <span className="modern-stats-item-value">{count}</span>
                      </div>
                      <div className="modern-stats-item-bar">
                        <div 
                          className="modern-stats-item-progress bg-secondary" 
                          style={{ width: `${getStatusPercentage(count)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Öncelik İstatistikleri */}
          <div className="col-lg-4 col-md-6 mb-4">
            <div className="modern-hr-widget">
              <div className="modern-hr-widget-header">
                <div className="modern-hr-widget-title">
                  <i className="bx bx-flag"></i>
                  <h4>Öncelik Dağılımı</h4>
                </div>
              </div>
              <div className="modern-hr-widget-body">
                <div className="modern-stats-list">
                  <div className="modern-stats-item">
                    <div className="modern-stats-item-info">
                      <span className="modern-stats-item-label">Düşük</span>
                      <span className="modern-stats-item-value">{priorityStats.low}</span>
                    </div>
                    <div className="modern-stats-item-bar">
                      <div 
                        className="modern-stats-item-progress bg-secondary" 
                        style={{ width: `${getStatusPercentage(priorityStats.low)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="modern-stats-item">
                    <div className="modern-stats-item-info">
                      <span className="modern-stats-item-label">Normal</span>
                      <span className="modern-stats-item-value">{priorityStats.normal}</span>
                    </div>
                    <div className="modern-stats-item-bar">
                      <div 
                        className="modern-stats-item-progress bg-primary" 
                        style={{ width: `${getStatusPercentage(priorityStats.normal)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="modern-stats-item">
                    <div className="modern-stats-item-info">
                      <span className="modern-stats-item-label">Yüksek</span>
                      <span className="modern-stats-item-value">{priorityStats.high}</span>
                    </div>
                    <div className="modern-stats-item-bar">
                      <div 
                        className="modern-stats-item-progress bg-warning" 
                        style={{ width: `${getStatusPercentage(priorityStats.high)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="modern-stats-item">
                    <div className="modern-stats-item-info">
                      <span className="modern-stats-item-label">Acil</span>
                      <span className="modern-stats-item-value">{priorityStats.urgent}</span>
                    </div>
                    <div className="modern-stats-item-bar">
                      <div 
                        className="modern-stats-item-progress bg-danger" 
                        style={{ width: `${getStatusPercentage(priorityStats.urgent)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default IdariIslerIstatistiklerPage;
